/**
 * @module features/client-device-management/types
 * @description Zod runtime validation schemas, TypeScript DTO contracts, and safe
 * formatting utilities for Super Admin Client Device Assignment & Configuration Management.
 */
import { z } from "zod";

// ============================================================================
// Core Enums & Literals (Aligned with Django TextChoices in models.py)
// ============================================================================

export const AssignmentStatusSchema = z
  .string()
  .transform((val) => val.toLowerCase())
  .pipe(z.enum(["active", "unassigned", "archived"]));
export type AssignmentStatus = z.infer<typeof AssignmentStatusSchema>;

export const UnassignModeSchema = z.enum(["wipe", "archive"]);
export type UnassignMode = z.infer<typeof UnassignModeSchema>;

export const ConfigDataTypeSchema = z
  .string()
  .transform((val) => val.toLowerCase())
  .pipe(z.enum(["string", "number", "boolean", "enum", "json"]));
export type ConfigDataType = z.infer<typeof ConfigDataTypeSchema>;

export const WifiAuditActionSchema = z
  .string()
  .transform((val) => val.toLowerCase())
  .pipe(z.enum(["assigned", "switched", "updated", "removed"]));
export type WifiAuditAction = z.infer<typeof WifiAuditActionSchema>;

// ============================================================================
// Pagination Schema
// ============================================================================

export const PaginationMetaSchema = z.object({
  page: z.number().int().default(1),
  page_size: z.number().int().default(10),
  total: z.number().int().optional(),
  total_count: z.number().int().optional(),
  total_pages: z.number().int().default(1),
  has_next: z.boolean().default(false),
  has_prev: z.boolean().default(false),
});
export type PaginationMeta = z.infer<typeof PaginationMetaSchema>;

// ============================================================================
// Assignment & Available Inventory Schemas
// ============================================================================

export const AvailableDeviceSchema = z.object({
  id: z.string(),
  device_name: z.string(),
  mac_address: z.string().nullable().optional(),
  registered_device_id: z.string(),
  device_type: z.string(),
  status: z.string(),
});
export type AvailableDevice = z.infer<typeof AvailableDeviceSchema>;

export const AssignmentSummarySchema = z.object({
  id: z.string(),
  client_id: z.string(),
  client_name: z.string(),
  device_id: z.string(),
  device_name: z.string(),
  mac_address: z.string().nullable().optional(),
  registered_device_id: z.string(),
  device_type: z.string(),
  status: AssignmentStatusSchema,
  custom_device_name: z.string().nullable().optional().transform((v) => v ?? ""),
  location: z.string().nullable().optional().transform((v) => v ?? ""),
  active_wifi_ssid: z.string().nullable().optional(),
  assigned_at: z.string().nullable().optional(),
  unassigned_at: z.string().nullable().optional(),
  created_by_name: z.string().nullable().optional(),
  unassigned_by_name: z.string().nullable().optional(),
  unassignment_mode: z.string().nullable().optional(),
  notes: z.string().nullable().optional().transform((v) => v ?? ""),
});
export type AssignmentSummary = z.infer<typeof AssignmentSummarySchema>;

export interface AssignDeviceInput {
  client_id: string;
  device_id: string;
  custom_device_name?: string;
  location?: string;
  notes?: string;
}

export interface UpdateAssignmentInput {
  custom_device_name?: string;
  location?: string;
  notes?: string;
  configs?: Record<string, string>;
}

export const EditAssignmentFormSchema = z.object({
  custom_device_name: z
    .string()
    .trim()
    .max(150, "Device friendly name cannot exceed 150 characters")
    .optional()
    .default(""),
  location: z
    .string()
    .trim()
    .max(255, "Deployment location cannot exceed 255 characters")
    .optional()
    .default(""),
  notes: z
    .string()
    .trim()
    .max(2000, "Operational notes cannot exceed 2,000 characters")
    .optional()
    .default(""),
});
export type EditAssignmentFormValues = z.infer<typeof EditAssignmentFormSchema>;

export interface BatchAssignInput {
  client_id: string;
  device_ids: string[];
  default_location?: string;
}

export const BatchAssignResponseSchema = z.object({
  total_requested: z.number().int(),
  assigned_count: z.number().int(),
  failed_count: z.number().int(),
  assignments: z.array(AssignmentSummarySchema).default([]),
  failures: z
    .array(
      z.object({
        device_id: z.string(),
        error: z.string(),
      })
    )
    .default([]),
});
export type BatchAssignResponse = z.infer<typeof BatchAssignResponseSchema>;

export const BulkCsvAssignResponseSchema = z.object({
  total_rows: z.number().int(),
  successful_assignments: z.number().int(),
  failed_assignments: z.number().int(),
  errors: z
    .array(
      z.object({
        line: z.number().int(),
        identifier: z.string(),
        error_message: z.string(),
      })
    )
    .default([]),
});
export type BulkCsvAssignResponse = z.infer<typeof BulkCsvAssignResponseSchema>;

export interface UnassignDeviceInput {
  mode: UnassignMode;
  reason?: string;
}

// ============================================================================
// Dynamic Parameter Catalog & Device Configurations Schemas
// ============================================================================

export const ValidationRuleSchema = z
  .object({
    min: z.number().optional(),
    max: z.number().optional(),
    min_length: z.number().optional(),
    max_length: z.number().optional(),
    regex: z.string().optional(),
    allowed_values: z.array(z.string()).optional(),
  })
  .passthrough()
  .default({});
export type ValidationRule = z.infer<typeof ValidationRuleSchema>;

export const DeviceConfigParameterSchema = z.object({
  config_key: z.string(),
  label: z.string(),
  description: z.string().nullable().optional().transform((v) => v ?? ""),
  data_type: ConfigDataTypeSchema,
  config_value: z.string().nullable().optional().transform((v) => v ?? ""),
  default_value: z.string().nullable().optional().transform((v) => v ?? ""),
  is_required: z.boolean().default(false),
  validation_rule: ValidationRuleSchema.nullable().optional().transform((v) => v ?? {}),
  display_order: z.number().int().default(0),
  updated_at: z.string().nullable().optional(),
});
export type DeviceConfigParameter = z.infer<typeof DeviceConfigParameterSchema>;

export const CatalogDefinitionSchema = z.object({
  id: z.string().optional(),
  device_type: z.string(),
  config_key: z.string(),
  label: z.string(),
  description: z.string().nullable().optional().transform((v) => v ?? ""),
  data_type: ConfigDataTypeSchema,
  default_value: z.string().nullable().optional().transform((v) => v ?? ""),
  is_required: z.boolean().default(false),
  validation_rule: ValidationRuleSchema.nullable().optional().transform((v) => v ?? {}),
  display_order: z.number().int().default(0),
});
export type CatalogDefinition = z.infer<typeof CatalogDefinitionSchema>;

// ============================================================================
// WiFi Network Vault, Single-Active Device Profile & Audit Log Schemas
// ============================================================================

export const WifiVaultItemSchema = z.object({
  id: z.string(),
  client_id: z.string().optional(),
  network_name: z.string(),
  ssid_name: z.string(),
  password: z.string().default("••••••••"),
  is_masked: z.boolean().default(true),
  is_active: z.boolean().default(true),
  notes: z.string().nullable().optional().transform((v) => v ?? ""),
  created_at: z.string().nullable().optional(),
  updated_at: z.string().nullable().optional(),
});
export type WifiVaultItem = z.infer<typeof WifiVaultItemSchema>;

export const CreateWifiVaultFormSchema = z.object({
  network_name: z
    .string()
    .trim()
    .min(1, "Profile name is required")
    .max(100, "Profile name cannot exceed 100 characters"),
  ssid_name: z
    .string()
    .trim()
    .min(1, "WiFi SSID is required")
    .max(32, "SSID cannot exceed 32 standard 802.11 characters"),
  password: z
    .string()
    .min(8, "Passphrase must be at least 8 characters for WPA2/WPA3 security")
    .max(64, "Passphrase cannot exceed 64 characters"),
  notes: z
    .string()
    .trim()
    .max(255, "Notes cannot exceed 255 characters")
    .optional()
    .default(""),
});
export type CreateWifiVaultFormValues = z.infer<typeof CreateWifiVaultFormSchema>;

export const UpdateWifiVaultFormSchema = z.object({
  network_name: z
    .string()
    .trim()
    .min(1, "Profile name is required")
    .max(100, "Profile name cannot exceed 100 characters"),
  ssid_name: z
    .string()
    .trim()
    .min(1, "WiFi SSID is required")
    .max(32, "SSID cannot exceed 32 standard 802.11 characters"),
  password: z
    .string()
    .optional()
    .default("")
    .refine(
      (val) => val === "" || (val.length >= 8 && val.length <= 64),
      "Passphrase must be 8–64 characters when changing it"
    ),
  notes: z
    .string()
    .trim()
    .max(255, "Notes cannot exceed 255 characters")
    .optional()
    .default(""),
});
export type UpdateWifiVaultFormValues = z.infer<typeof UpdateWifiVaultFormSchema>;

export const DeviceWifiProfileSchema = z.object({
  id: z.string(),
  assignment_id: z.string(),
  client_wifi_network_id: z.string().nullable().optional(),
  network_name: z.string().nullable().optional(),
  ssid_name: z.string(),
  password: z.string().default("••••••••"),
  is_masked: z.boolean().default(true),
  is_active: z.boolean().default(true),
  notes: z.string().nullable().optional().transform((v) => v ?? ""),
  created_at: z.string().nullable().optional(),
  updated_at: z.string().nullable().optional(),
});
export type DeviceWifiProfile = z.infer<typeof DeviceWifiProfileSchema>;

export interface UpdateDeviceWifiInput {
  client_wifi_network_id?: string;
  custom_ssid_name?: string;
  custom_password?: string;
  notes?: string;
}

export const CustomDeviceWifiFormSchema = z.object({
  custom_ssid_name: z
    .string()
    .trim()
    .min(1, "WiFi SSID is required")
    .max(32, "SSID cannot exceed 32 characters"),
  custom_password: z
    .string()
    .min(8, "Passphrase must be at least 8 characters")
    .max(64, "Passphrase cannot exceed 64 characters"),
  notes: z
    .string()
    .trim()
    .max(255, "Notes cannot exceed 255 characters")
    .optional()
    .default(""),
});
export type CustomDeviceWifiFormValues = z.infer<typeof CustomDeviceWifiFormSchema>;

export const EditCustomDeviceWifiFormSchema = z.object({
  custom_ssid_name: z
    .string()
    .trim()
    .min(1, "WiFi SSID is required")
    .max(32, "SSID cannot exceed 32 characters"),
  custom_password: z
    .string()
    .optional()
    .default("")
    .refine(
      (val) => val === "" || (val.length >= 8 && val.length <= 64),
      "Passphrase must be 8–64 characters when changing it"
    ),
  notes: z
    .string()
    .trim()
    .max(255, "Notes cannot exceed 255 characters")
    .optional()
    .default(""),
});
export type EditCustomDeviceWifiFormValues = z.infer<
  typeof EditCustomDeviceWifiFormSchema
>;

export const RevealedPasswordSchema = z.object({
  plaintext_password: z.string(),
});

export const WifiAuditItemSchema = z.object({
  id: z.string(),
  assignment_id: z.string().optional(),
  device_id: z.string().optional(),
  action: WifiAuditActionSchema,
  previous_wifi_network_id: z.string().nullable().optional(),
  previous_ssid_name: z.string().nullable().optional().transform((v) => v ?? ""),
  new_wifi_network_id: z.string().nullable().optional(),
  new_ssid_name: z.string().nullable().optional().transform((v) => v ?? ""),
  changed_by_name: z.string().default("System"),
  changed_at: z.string(),
  reason: z.string().nullable().optional().transform((v) => v ?? ""),
});
export type WifiAuditItem = z.infer<typeof WifiAuditItemSchema>;

// ============================================================================
// Quotas & Fleet Statistics Schemas
// ============================================================================

export const QuotaItemSchema = z.object({
  id: z.string(),
  client_id: z.string(),
  device_type: z.string(),
  max_limit: z.number().int().min(1),
  current_assigned_count: z.number().int().default(0),
  created_at: z.string().nullable().optional(),
  updated_at: z.string().nullable().optional(),
});
export type QuotaItem = z.infer<typeof QuotaItemSchema>;

export const SetQuotaFormSchema = z.object({
  device_type: z.string().min(1, "Device type is required"),
  max_limit: z.coerce
    .number()
    .int("Quota must be a whole number")
    .min(1, "Quota limit must be at least 1")
    .max(100000, "Quota limit cannot exceed 100,000"),
});
export type SetQuotaFormValues = z.infer<typeof SetQuotaFormSchema>;

export const FleetAssignmentStatsSchema = z.object({
  total_assigned_devices: z.number().int().default(0),
  total_available_devices: z.number().int().default(0),
  total_client_organizations: z.number().int().default(0),
  by_device_type: z.record(z.string(), z.number()).default({}),
});
export type FleetAssignmentStats = z.infer<typeof FleetAssignmentStatsSchema>;

// ============================================================================
// Pure Domain & Security Helper Utilities
// ============================================================================

/**
 * Validates dynamic image URLs before passing them to `<img src>` to prevent
 * `javascript:` or unsafe URI injection (OWASP XSS protection).
 *
 * @param rawUrl - Candidate logo URL returned from the API.
 * @returns Sanitized URL string if safe, or null to trigger monogram fallback.
 */
export function sanitizeLogoUrl(rawUrl?: string | null): string | null {
  if (!rawUrl || typeof rawUrl !== "string") return null;
  const trimmed = rawUrl.trim();
  if (!trimmed) return null;

  // Backend logo_service may emit inline SVG monograms using data:image/svg+xml;
  // reject any SVG data URI containing script tags, event handlers, or javascript:
  if (trimmed.toLowerCase().startsWith("data:image/")) {
    let decoded = trimmed;
    try {
      decoded = decodeURIComponent(trimmed);
    } catch {
      // Keep raw string if not percent-encoded
    }
    if (/(<script\b|javascript:|on[a-z]+\s*=)/i.test(decoded)) {
      return null;
    }
    return trimmed;
  }
  // Allow relative media paths served by Django static/media
  if (trimmed.startsWith("/")) {
    return trimmed;
  }

  try {
    const parsed = new URL(trimmed);
    if (parsed.protocol === "https:" || parsed.protocol === "http:") {
      return parsed.toString();
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Resolves a machine device_type key (e.g. `display_module` or future gateway keys)
 * into a human-readable label using registered descriptors with graceful title-case fallback.
 *
 * @param deviceType - Raw device_type identifier.
 * @param descriptors - Optional list of descriptors fetched from `/super-admin/devices/types/`.
 * @returns Human-friendly device category label.
 */
export function formatDeviceTypeLabel(
  deviceType: string,
  descriptors?: ReadonlyArray<{ type_id: string; label: string }>
): string {
  if (!deviceType) return "Unknown Device";
  const matched = descriptors?.find((d) => d.type_id === deviceType);
  if (matched?.label) return matched.label;

  return deviceType
    .split("_")
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1).toLowerCase())
    .join(" ");
}
